import React, { useState } from 'react'
import moment from 'moment'
import DashboardEditableRow from '@components/pages/Dashboard/DashboardEditableRow.component'
import { CALENDAR_DATE_FORMAT } from '@utils/dashboardFinance.util'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { DashboardSection } from '@constants/Dashboard/dashboard.enums'
import { useFinanceDashboard } from './useFinanceDashboard'
import {
  ARAgingCard,
  ARAgingMetricCard,
  AROver90DayCard,
  ClaimsCard,
  CollectionCard,
  CollectionMetricCard,
  CollectionPerformanceCard,
  CollectionRateMetricCard,
  CollectionRatioCard,
  DentistHygienistProductionCard,
  InsurancePatientCollectionCard,
  NetHygieneProductionCard,
  OutstandingBalanceCard,
  PracticeRevenueOverviewCard,
  ProductionByDepartmentCard,
  ProductionByProcedureCard,
  ProductionCard,
  ProductionPerHygieneVisitCard,
  ProductionPerProviderCard,
  TreatmentValueCard,
} from './SectionCards'
import '@components/pages/Dashboard/DashboardLayout.style.css'
import '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.style.css'
import './DashboardFinance.style.css'

type DashboardFinanceProps = {
  editMode?: boolean
  hiddenSections?: Set<string>
  /** Anchor date in `MMMM D, YYYY` format (dashboard header). */
  date?: string
  range?: RangeKey
}

function DashboardFinance({
  editMode = false,
  hiddenSections = new Set(),
  date = moment().format(CALENDAR_DATE_FORMAT),
  range = '1D',
}: DashboardFinanceProps): React.ReactElement {
  const show = (section: DashboardSection): boolean =>
    !hiddenSections.has(section)
  const [providerSearch, setProviderSearch] = useState('')

  const finance = useFinanceDashboard({
    range,
    date,
    hiddenSections,
    providerSearch,
  })

  return (
    <div className="dashboard-finance">
      {show(DashboardSection.PracticeRevenueOverview) && (
        <PracticeRevenueOverviewCard
          titleHref={finance.hrefs.practiceRevenueOverview}
          practiceRevenue={finance.practiceRevenueApi}
        />
      )}

      {(show(DashboardSection.Production) ||
        show(DashboardSection.CollectionPerformance)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row dashboard-finance-production-collection-row"
        >
          {show(DashboardSection.Production) && (
            <ProductionCard
              titleHref={finance.hrefs.production}
              range={range}
              data={finance.prodPanel}
              loading={finance.productionLoading}
              hasPayload={finance.productionHasPayload}
              hasData={finance.productionHasData}
              hasChartData={finance.productionHasChartData}
            />
          )}
          {show(DashboardSection.CollectionPerformance) && (
            <CollectionPerformanceCard
              titleHref={finance.hrefs.collectionPerformance}
              range={range}
              collection={finance.collectionApi}
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.InsuranceVsPatientCollection) ||
        show(DashboardSection.DentistVsHygienistProduction)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row dashboard-finance-three-cards-row"
        >
          {show(DashboardSection.InsuranceVsPatientCollection) && (
            <InsurancePatientCollectionCard
              titleHref={finance.hrefs.insurancePatientCollection}
              collection={finance.collectionApi}
            />
          )}
          {show(DashboardSection.DentistVsHygienistProduction) && (
            <DentistHygienistProductionCard
              titleHref={finance.hrefs.dentistHygienistProduction}
              data={finance.prodByDeptApi}
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.TreatmentValue) ||
        show(DashboardSection.ArAging)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row dashboard-finance-ar-row"
        >
          {show(DashboardSection.TreatmentValue) && (
            <TreatmentValueCard
              titleHref={finance.hrefs.treatmentValue}
              treatmentValue={finance.treatmentValueApi}
            />
          )}
          {show(DashboardSection.ArAging) && (
            <ARAgingCard
              titleHref={finance.hrefs.arAging}
              range={range}
              arAging={finance.arAgingApi}
            />
          )}
        </DashboardEditableRow>
      )}

      {show(DashboardSection.OutstandingBalance) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row dashboard-finance-outstanding-claims-row"
        >
          <OutstandingBalanceCard
            titleHref={finance.hrefs.outstandingBalance}
            outstanding={finance.outstandingApi}
          />
        </DashboardEditableRow>
      )}

      {show(DashboardSection.Claims) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row"
        >
          <ClaimsCard
            titleHref={finance.hrefs.financeClaims}
            claims={finance.claimsApi}
          />
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.Collection) ||
        show(DashboardSection.CollectionRatio)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row dashboard-finance-collection-row"
        >
          {show(DashboardSection.Collection) && (
            <CollectionCard
              titleHref={finance.hrefs.financeCollection}
              range={range}
              periodLabel={finance.periodLabel}
              collection={finance.collectionApi}
            />
          )}
          {show(DashboardSection.CollectionRatio) && (
            <CollectionRatioCard
              titleHref={finance.hrefs.financeCollectionRatio}
              collection={finance.collectionApi}
            />
          )}
        </DashboardEditableRow>
      )}

      {show(DashboardSection.ProductionByDepartment) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row"
        >
          <ProductionByDepartmentCard
            productionByDepartmentHref={
              finance.hrefs.financeProductionByDepartment
            }
            productionPerHygieneVisitHref={
              finance.hrefs.productionPerHygieneVisit
            }
            range={range}
            data={finance.prodByDeptApi}
            isAllView
          />
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.NetHygieneProductionFinance) ||
        show(DashboardSection.ProductionPerHygieneVisitFinance)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-four-cards-row"
        >
          {show(DashboardSection.NetHygieneProductionFinance) && (
            <NetHygieneProductionCard
              titleHref={finance.hrefs.netHygieneProduction}
              metric={finance.hygieneApi.netHygieneMetric}
              loading={finance.hygieneApi.loading}
              hasPayload={finance.hygieneApi.hasPayload}
              hasData={finance.hygieneApi.hasNetHygieneData}
            />
          )}
          {show(DashboardSection.ProductionPerHygieneVisitFinance) && (
            <ProductionPerHygieneVisitCard
              titleHref={finance.hrefs.productionPerHygieneVisit}
              metric={finance.hygieneApi.productionPerVisitMetric}
              loading={finance.hygieneApi.loading}
              hasPayload={finance.hygieneApi.hasPayload}
              hasData={finance.hygieneApi.hasProductionPerVisitData}
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.CollectionMetric) ||
        show(DashboardSection.CollectionRate) ||
        show(DashboardSection.ArAgingMetric) ||
        show(DashboardSection.ArOver90Day)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row dashboard-finance-four-metrics-row"
        >
          {show(DashboardSection.CollectionMetric) && (
            <CollectionMetricCard
              titleHref={finance.hrefs.financeCollection}
              collection={finance.collectionApi}
            />
          )}
          {show(DashboardSection.CollectionRate) && (
            <CollectionRateMetricCard
              titleHref={finance.hrefs.financeCollection}
              collection={finance.collectionApi}
            />
          )}
          {show(DashboardSection.ArAgingMetric) && (
            <ARAgingMetricCard
              titleHref={finance.hrefs.arAging}
              arAging={finance.arAgingApi}
            />
          )}
          {show(DashboardSection.ArOver90Day) && (
            <AROver90DayCard
              titleHref={finance.hrefs.arOver90Day}
              arOver90={finance.arOver90Api}
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.ProductionByProcedure) ||
        show(DashboardSection.ProductionPerProvider)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-two-cards-row"
        >
          {show(DashboardSection.ProductionByProcedure) && (
            <ProductionByProcedureCard
              titleHref={finance.hrefs.productionByProcedure}
              data={finance.prodByProcedureApi}
            />
          )}
          {show(DashboardSection.ProductionPerProvider) && (
            <ProductionPerProviderCard
              titleHref={finance.hrefs.productionPerProvider}
              data={finance.prodPerProviderApi}
              providerSearch={providerSearch}
              onProviderSearchChange={setProviderSearch}
            />
          )}
        </DashboardEditableRow>
      )}
    </div>
  )
}

export default DashboardFinance
